<template>
  <div
    class="base-button"
    v-tooltip="{ content: tooltipText }"
    :class="[{ 'is-full-width': isFullWidth }, `variant-${variant}`]"
  >
    <a
      :href="href"
      :target="targetBlank ? '_blank' : '_self'"
      :rel="targetBlank ? 'noopener noreferrer' : null"
      class="base-button__element"
      v-bind:class="{
        'has-icon-left': hasIconLeft,
        'has-icon-right': hasIconRight,
        'is-outlined': isOutlined,
        'is-small': isSmall,
        'is-large': isLarge,
        'is-ai': isAi,
        'is-square': isSquare,
        'is-grey': isGrey,
      }"
      v-if="href !== ''"
    >
      <slot>Text</slot>
    </a>

    <router-link
      :to="to"
      class="base-button__element"
      v-bind:class="{
        'has-icon-left': hasIconLeft,
        'has-icon-right': hasIconRight,
        'is-outlined': isOutlined,
        'is-small': isSmall,
        'is-large': isLarge,
        'is-ai': isAi,
        'is-square': isSquare,
        'is-grey': isGrey,
      }"
      v-else-if="to !== ''"
    >
      <slot>Text</slot>
    </router-link>

    <!--
      TODO (tech debt): @mousedown.prevent/@pointerdown.prevent stop the button from
      grabbing focus on mouse click, but they also stop the mousedown reaching
      document-level listeners — so any `v-closable` popover won't close when a
      BaseButton is clicked (see PageTenders.closeFilterPopovers for a per-feature
      workaround). Preferred fix: drop these and hide the ring via :focus-visible CSS,
      keeping focus intact. App-wide change — needs visual QA before adopting.
    -->
    <button
      @mousedown.prevent
      @pointerdown.prevent
      :disabled="disabled"
      :type="type"
      class="base-button__element"
      role="button"
      v-bind:class="{
        'has-icon-left': hasIconLeft,
        'has-icon-right': hasIconRight,
        'is-loading': isLoading,
        'is-outlined': isOutlined,
        'is-transparent': isTransparent,
        'is-danger': isDanger,
        'is-ai': isAi,
        'is-small': isSmall,
        'is-large': isLarge,
        'is-underline': isUnderline,
        'is-square': isSquare,
        'is-grey': isGrey,
      }"
      v-else
    >
      <slot>Text</slot>
    </button>
  </div>
</template>

<script>
export default {
  name: 'BaseButton',
  props: {
    disabled: Boolean,
    hasIconLeft: Boolean,
    hasIconRight: Boolean,
    isLoading: Boolean,
    isOutlined: Boolean,
    isTransparent: Boolean,
    isUnderline: Boolean,
    isSquare: Boolean,
    isDanger: Boolean,
    isAi: Boolean,
    isSmall: Boolean,
    isLarge: Boolean,
    isFullWidth: Boolean,
    targetBlank: Boolean,
    type: {
      type: String,
      default: 'button',
      required: false,
    },
    to: {
      type: [Object, String],
      default: '',
      required: false,
    },
    href: {
      type: String,
      default: '',
      required: false,
    },
    isGrey: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
      required: false,
    },
    variant: {
      type: String,
      required: false,
      default: 'primary',
    },
  },
};
</script>

<style lang="scss">
.base-button {
  display: inline-block;
  user-select: none;

  &.is-full-width {
    width: 100%;

    .base-button__element {
      width: 100%;
      justify-content: center;
    }
  }

  &.variant-secondary {
    .base-button__element {
      background-color: $color-semantic-ai;
      color: $color-white;
    }
  }

  &__element {
    align-items: center;
    align-self: center;
    border: 0;
    background-color: $color-accent;
    color: $color-white;
    border-radius: $border-radius-large;
    cursor: pointer;
    display: inline-flex;
    font-size: 16px;
    min-height: 44px;
    outline: none;
    padding: 8px 16px;
    text-decoration: none;
    transition: all 200ms ease;

    &:hover,
    &:active {
      background-color: $color-accent-700;
      color: $color-white;
    }

    &:focus {
      box-shadow: $shadow-focus-outline;
      color: $color-white;
    }

    &:disabled {
      box-shadow: none;
      cursor: not-allowed;
      opacity: 0.6;
    }

    &.is-loading {
      .feather {
        animation: rotation 1.4s infinite linear;
      }
    }

    &.has-icon-right {
      > .feather {
        flex: none;
        height: 20px;
        margin-left: 8px;
        width: 20px;
      }
    }

    &.has-icon-left {
      > .feather {
        flex: none;
        height: 20px;
        margin-right: 8px;
        width: 20px;
      }

      .base-button__loader {
        margin: 0 8px 0 0;
      }
    }

    &.is-small {
      border-radius: $border-radius-large;
      font-size: 14px;
      min-height: 20px;
      padding: 4px 12px;

      &.has-icon-left {
        > .feather {
          flex: none;
          height: 14px;
          margin-right: 4px;
          width: 14px;
        }
      }

      &.has-icon-right {
        > .feather {
          flex: none;
          height: 14px;
          margin-left: 4px;
          width: 14px;
        }
      }
    }

    &.is-large {
      justify-content: center;
      // Never wider than where it sits: a phone's login card is narrower than 300px.
      min-width: min(300px, 100%);
    }

    &.is-outlined {
      background-color: transparent;
      border: 1px solid $color-border;
      color: $color-normal;

      &:hover,
      &:active {
        border-color: $color-accent;
        color: $color-normal;
      }
    }

    &.is-danger {
      color: $color-white;
      background-color: $color-error;

      &:hover {
        background: $color-pale-error;
      }
    }

    // AI tone: purple gradient fill (see $gradient-ai). Uses `background`, not
    // `background-color`, so the gradient is valid.
    &.is-ai {
      background: $gradient-ai;
      color: $color-white;

      &:hover,
      &:active {
        background: $gradient-ai-hover;
        color: $color-white;
      }
    }

    &.is-square {
      border-radius: $border-radius-root;
      padding: 8px 16px;

      &.is-small {
        border-radius: $border-radius-root;
        padding: 4px 8px;
      }
    }

    &.is-transparent {
      background-color: transparent;
      color: $color-normal;

      &:hover,
      &:active {
        border-color: $color-accent;
        color: $color-normal;
        background-color: $color-hover;
      }
    }

    &.is-outlined.is-transparent {
      border: 1px solid $color-neutral;

      &:hover {
        border: 1px solid $color-neutral-pale !important;
      }
    }

    &.is-underline {
      font-weight: bold;
      text-decoration: underline;

      &:hover {
        text-decoration: underline;
      }
    }
    &.is-grey {
      background-color: $color-neutral-pale;
      font-weight: bold;
      color: $color-normal;

      &:hover {
        background-color: $color-hover;
      }
    }
  }

  &__loader {
    border: 1px solid currentColor;
    border-radius: $border-radius-round;
    border-right-color: transparent;
    display: inline-block;
    height: 20px;
    margin-left: 8px;
    width: 20px;
  }
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}
</style>
